import { useApolloClient } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { useTolgee } from "@tolgee/react";
import React from "react";
import { Capabilities, PermissionName } from "../../clients/graphqlTypes.js";
import { UserDataContext } from "./UserDataContext.js";
import {
  AuthUpdateUserMutationVariables,
  useAuthUpdateUserMutation,
  useMeQuery,
} from "./UserDataProvider.generated.js";
import {
  RICHTER_FRENZEL_LOCALE_OVERRIDES,
  RICHTER_FRENZEL_ORGANISATION_IDS,
} from "../../common/translations/richterFrenzel.js";

export const UserDataProvider = ({ children }: React.PropsWithChildren<{}>) => {
  const tolgee = useTolgee(["language"]);

  const client = useApolloClient();
  const meQuery = useMeQuery({ client, variables: {} });
  const [updateUserMutation] = useAuthUpdateUserMutation({ client });

  const userData = meQuery.data?.viewer ?? null;

  const hasCapability = React.useCallback(
    (capability: Capabilities) => {
      if (!userData) return false;

      return userData.organisation.capabilities.includes(capability);
    },
    [userData]
  );

  const hasOrganisationPermission = React.useCallback(
    (permissionName: PermissionName) => {
      if (!userData) return false;

      return userData.organisationPermissions.includes(permissionName);
    },
    [userData]
  );

  const updateUser = React.useCallback(
    async function updateUser(variables: AuthUpdateUserMutationVariables) {
      await updateUserMutation({ variables });
      await meQuery.refetch();
    },
    [meQuery, updateUserMutation]
  );

  React.useEffect(() => {
    if (userData?.locale) {
      const userLocale = RICHTER_FRENZEL_ORGANISATION_IDS.includes(
        userData.organisation.id
      )
        ? (RICHTER_FRENZEL_LOCALE_OVERRIDES[userData.locale] ?? userData.locale)
        : userData.locale;

      if (userLocale !== tolgee.getLanguage()) {
        tolgee.changeLanguage(userLocale);
      }
    }
  }, [tolgee, userData?.locale, userData?.organisation.id]);

  React.useEffect(() => {
    // v8: `configureScope` was removed — set user/tags on the current scope.
    if (userData) {
      Sentry.setUser({ id: userData.id });
      Sentry.setTag("organisation_id", userData.organisation.id);
    } else {
      Sentry.setUser(null);
      Sentry.setTag("organisation_id", undefined);
    }
  }, [userData]);

  React.useEffect(() => {
    function windowFocusHandler() {
      meQuery.refetch();
    }

    window.addEventListener("focus", windowFocusHandler);

    return () => {
      window.removeEventListener("focus", windowFocusHandler);
    };
  }, [meQuery]);

  const installerRole = "ORG_MEMBER";

  const appViewerRole = !userData
    ? null
    : userData.roles.every(role => role.internalName === installerRole)
      ? "INSTALLER"
      : "MAIN";

  return (
    <UserDataContext.Provider
      value={{
        isLoading: meQuery.loading && meQuery.data === undefined,
        currentUser: userData,
        appViewerRole,
        isOrganisationAdmin: userData?.isAdmin ?? false,
        hasCapability,
        hasOrganisationPermission,
        updateUser,
        refetchMe: meQuery.refetch,
      }}
    >
      {children}
    </UserDataContext.Provider>
  );
};
